import React from "react";
import Card from "components/card";
import { useAuth } from "context/AuthContext";
import CertificateDownloadModal from "./CertificateDownloadModal";
import IntegracionesTab from "./IntegracionesTab";
import {
  authApi,
  emissionProfilesApi,
  organizationApiKeysApi,
  organizationMembersApi,
  organizationsApi,
} from "api/endpoints";
import { useShallow } from "zustand/react/shallow";
import { useEmissionAmbienteStore } from "stores/emissionAmbienteStore";

const ORG_STATUS_OPTIONS = [
  { value: "active", label: "Activa" },
  { value: "inactive", label: "Inactiva" },
  { value: "suspended", label: "Suspendida" },
];

const PROFILE_STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "inactive", label: "Inactivo" },
];

const PROFILE_TABS = [
  { id: "perfil", label: "Perfil" },
  { id: "perfiles_emision", label: "Perfiles de Emisión" },
  { id: "miembros", label: "Miembros" },
  { id: "api_keys", label: "API Keys" },
  { id: "integraciones", label: "Integraciones" },
];

const MEMBER_ROLE_LABELS = {
  owner: "Dueño",
  admin: "Administrador",
  contador: "Contador",
  viewer: "Solo lectura",
};

// Roles que se pueden asignar. 'owner' NO está: es el dueño de la empresa y
// se resuelve por otra vía. El backend lo rechaza igual; acá ni se ofrece.
const MEMBER_ROLE_OPTIONS = [
  { value: "contador", label: "Contador", hint: "Opera documentos y compras. No borra CAF." },
  { value: "admin", label: "Administrador", hint: "Control total sobre la empresa." },
  { value: "viewer", label: "Solo lectura", hint: "Solo puede mirar y exportar." },
];

const API_KEY_SCOPE_OPTIONS = [
  { value: "*", label: "Acceso total (*)" },
  { value: "emit_factura", label: "Emitir factura" },
  { value: "emit_boleta", label: "Emitir boleta" },
  { value: "emit_guia", label: "Emitir guía" },
  { value: "emit_nota_credito", label: "Emitir nota de crédito" },
  { value: "emit_nota_debito", label: "Emitir nota de débito" },
  { value: "emit_libro", label: "Emitir/enviar libros" },
  { value: "manage_caf", label: "Gestionar CAF" },
  { value: "manage_profiles", label: "Gestionar perfiles" },
  { value: "read_documents", label: "Leer documentos" },
];

const SURFACE_PANEL =
  "rounded-2xl border border-gray-200 bg-lightPrimary/40 p-4 dark:border-white/10 dark:bg-navy-900/30";
const INPUT_BASE =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 transition focus:border-brand-500 focus:outline-none dark:border-white/15 dark:bg-navy-900 dark:text-white";
const LABEL_BASE = "text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300";
const BTN_PRIMARY =
  "rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300";
const BTN_SECONDARY =
  "rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-navy-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800";
const BTN_DANGER =
  "rounded-lg border border-red-400 bg-white px-2.5 py-1.5 font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-400 dark:bg-navy-900 dark:text-red-300 dark:hover:bg-navy-800";

function normalizeOrgStatus(v) {
  const s = String(v || "").trim().toLowerCase();
  if (s === "inactive" || s === "suspended") return s;
  return "active";
}

function normalizeProfileStatus(v) {
  const s = String(v || "").trim().toLowerCase();
  return s === "inactive" ? "inactive" : "active";
}

function normalizeAmbiente(v) {
  const s = String(v || "").trim().toLowerCase();
  return s === "produccion" ? "produccion" : "certificacion";
}

function parseCompanyEmails(rawValue) {
  return String(rawValue || "")
    .split(/[,\n;]/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function formatCompanyEmails(value) {
  if (Array.isArray(value)) return value.join("\n");
  return "";
}

function normalizeEmisorForForm(profile) {
  const em = profile?.emisor && typeof profile.emisor === "object" ? profile.emisor : {};
  return {
    rznSoc: String(em.RznSoc || ""),
    giroEmis: String(em.GiroEmis || ""),
    dirOrigen: String(em.DirOrigen || ""),
    cmnaOrigen: String(em.CmnaOrigen || ""),
    ciudadOrigen: String(em.CiudadOrigen || ""),
    nroResol: String(em.NroResol || ""),
    fchResol: String(em.FchResol || ""),
  };
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" });
}

export default function ProfileOverview() {
  const { user, auth, currentOrganization, refreshSession, isReadOnly } = useAuth();
  const [certDownloadOpen, setCertDownloadOpen] = React.useState(false);
  const emission = useEmissionAmbienteStore(
    useShallow((s) => ({
      kind: s.kind,
      profileUuid: s.profileUuid,
      setSelectedProfileUuid: s.setSelectedProfileUuid,
    })),
  );

  const [loadingData, setLoadingData] = React.useState(true);
  const [organizations, setOrganizations] = React.useState([]);
  const [profiles, setProfiles] = React.useState([]);
  const [selectedOrgUuid, setSelectedOrgUuid] = React.useState("");
  const [selectedProfileId, setSelectedProfileId] = React.useState("");
  const [feedback, setFeedback] = React.useState({ type: "", text: "" });
  const [activeTab, setActiveTab] = React.useState("perfil");
  const [members, setMembers] = React.useState([]);
  const [membersLoading, setMembersLoading] = React.useState(false);
  const [memberSavingId, setMemberSavingId] = React.useState(null);

  const [orgForm, setOrgForm] = React.useState({
    name: "",
    estado: "active",
  });
  const [profileForm, setProfileForm] = React.useState({
    profile_name: "",
    nombre_firmante: "",
    company_phone: "",
    company_emails_text: "",
    estado: "active",
    ambiente: "certificacion",
    is_default: false,
  });
  const [emisorForm, setEmisorForm] = React.useState({
    rznSoc: "",
    giroEmis: "",
    dirOrigen: "",
    cmnaOrigen: "",
    ciudadOrigen: "",
    nroResol: "",
    fchResol: "",
  });
  const [passwordForm, setPasswordForm] = React.useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [certificateForm, setCertificateForm] = React.useState({
    file: null,
    certificatePassword: "",
  });
  const [productionGuard, setProductionGuard] = React.useState({
    confirmReadImpact: false,
    confirmReadiness: false,
    confirmSandboxMode: false,
    authPassword: "",
    typedConfirmation: "",
  });
  const [ambienteSwitchModal, setAmbienteSwitchModal] = React.useState({
    open: false,
    step: 1,
    targetAmbiente: "certificacion",
  });
  const [apiKeyForm, setApiKeyForm] = React.useState({
    name: "",
    expiresAt: "",
    includeRevoked: false,
    selectedScopes: ["read_documents"],
  });
  const [apiKeySearch, setApiKeySearch] = React.useState("");
  const [apiKeys, setApiKeys] = React.useState([]);
  const [apiKeysLoading, setApiKeysLoading] = React.useState(false);
  const [apiKeysBusyById, setApiKeysBusyById] = React.useState({});
  const [plainKeyNotice, setPlainKeyNotice] = React.useState("");

  const [savingOrg, setSavingOrg] = React.useState(false);
  const [savingProfile, setSavingProfile] = React.useState(false);
  const [deletingProfile, setDeletingProfile] = React.useState(false);
  const [savingPassword, setSavingPassword] = React.useState(false);
  const [savingCertificate, setSavingCertificate] = React.useState(false);
  const [savingApiKey, setSavingApiKey] = React.useState(false);

  const selectedOrg = React.useMemo(
    () =>
      organizations.find(
        (o) => String(o.organization_uuid) === String(selectedOrgUuid),
      ) || null,
    [organizations, selectedOrgUuid],
  );

  const orgProfiles = React.useMemo(() => {
    if (!selectedOrg?.id) return [];
    return profiles
      .filter((p) => Number(p.organization_id) === Number(selectedOrg.id))
      .sort((a, b) => Number(b.is_default) - Number(a.is_default));
  }, [profiles, selectedOrg]);

  const selectedProfile = React.useMemo(
    () =>
      orgProfiles.find((p) => String(p.id) === String(selectedProfileId)) || null,
    [orgProfiles, selectedProfileId],
  );
  const currentAmbiente = React.useMemo(
    () => normalizeAmbiente(selectedProfile?.ambiente),
    [selectedProfile?.ambiente],
  );
  const requestedAmbiente = React.useMemo(
    () => normalizeAmbiente(profileForm.ambiente),
    [profileForm.ambiente],
  );
  const isAmbienteSwitchRequested = currentAmbiente !== requestedAmbiente;
  const isPromotionToProduction = isAmbienteSwitchRequested && requestedAmbiente === "produccion";
  const isDemotionToCertification = isAmbienteSwitchRequested && requestedAmbiente === "certificacion";
  const ambienteTargetLabel = requestedAmbiente === "produccion" ? "Producción" : "Certificación";
  const switchButtonLabel =
    currentAmbiente === "produccion"
      ? "Solicitar cambio a Certificación"
      : "Solicitar cambio a Producción";
  const modalTargetAmbiente = normalizeAmbiente(ambienteSwitchModal.targetAmbiente);
  const modalTargetLabel = modalTargetAmbiente === "produccion" ? "Producción" : "Certificación";
  const isModalPromotionToProduction = modalTargetAmbiente === "produccion";
  const modalExpectedWord = isModalPromotionToProduction ? "PRODUCCION" : "CERTIFICACION";
  const modalStep = Number(ambienteSwitchModal.step || 1);
  const modalTotalSteps = 4;
  const modalResolutionCheck = React.useMemo(() => {
    const requiredNroResol = String(emisorForm.nroResol || "").trim();
    const requiredFchResol = String(emisorForm.fchResol || "").trim();
    const currentNroResol = String(selectedProfile?.emisor?.NroResol || "").trim();
    const currentFchResol = String(selectedProfile?.emisor?.FchResol || "").trim();
    const hasResolution = Boolean(requiredNroResol && requiredFchResol);
    const hasNewResolution =
      requiredNroResol !== currentNroResol || requiredFchResol !== currentFchResol;
    return {
      hasResolution,
      hasNewResolution,
      requiredNroResol,
      requiredFchResol,
    };
  }, [emisorForm, selectedProfile?.emisor?.FchResol, selectedProfile?.emisor?.NroResol]);

  const canAdvanceModalStep = React.useMemo(() => {
    if (modalStep === 1) return Boolean(productionGuard.confirmReadImpact);
    if (modalStep === 2) {
      if (!modalResolutionCheck.hasResolution || !modalResolutionCheck.hasNewResolution) return false;
      if (isModalPromotionToProduction) {
        return Boolean(selectedProfile?.is_default && productionGuard.confirmReadiness);
      }
      return Boolean(productionGuard.confirmSandboxMode);
    }
    const hasPassword = Boolean(String(productionGuard.authPassword || "").trim());
    const hasTyped = String(productionGuard.typedConfirmation || "").trim().toUpperCase() === modalExpectedWord;
    const hasAuth = Boolean(hasPassword && hasTyped);
    if (modalStep === 3) return hasAuth;
    if (modalStep === 4) return hasAuth;
    return false;
  }, [
    modalStep,
    productionGuard,
    modalResolutionCheck,
    isModalPromotionToProduction,
    selectedProfile?.is_default,
    modalExpectedWord,
  ]);
  const modalChecklist = React.useMemo(() => {
    const readImpact = Boolean(productionGuard.confirmReadImpact);
    const resolutionReady = Boolean(
      modalResolutionCheck.hasResolution && modalResolutionCheck.hasNewResolution,
    );
    const defaultReady = isModalPromotionToProduction ? Boolean(selectedProfile?.is_default) : true;
    const flowReady = isModalPromotionToProduction
      ? Boolean(productionGuard.confirmReadiness)
      : Boolean(productionGuard.confirmSandboxMode);
    const authReady =
      Boolean(String(productionGuard.authPassword || "").trim())
      && String(productionGuard.typedConfirmation || "").trim().toUpperCase() === modalExpectedWord;
    const allReady = readImpact && resolutionReady && defaultReady && flowReady && authReady;
    return {
      readImpact,
      resolutionReady,
      defaultReady,
      flowReady,
      authReady,
      allReady,
    };
  }, [
    productionGuard,
    modalResolutionCheck,
    isModalPromotionToProduction,
    selectedProfile?.is_default,
    modalExpectedWord,
  ]);
  const hasAmbienteModalProgress = React.useMemo(
    () =>
      modalStep > 1
      || Boolean(productionGuard.confirmReadImpact)
      || Boolean(productionGuard.confirmReadiness)
      || Boolean(productionGuard.confirmSandboxMode)
      || Boolean(String(productionGuard.authPassword || "").trim())
      || Boolean(String(productionGuard.typedConfirmation || "").trim()),
    [modalStep, productionGuard],
  );
  const renderModalChecklistItem = React.useCallback((label, state) => {
    const isOk = state === "ok";
    const isBlocked = state === "blocked";
    const iconClass = isOk
      ? "border-emerald-300 bg-emerald-100 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-900/30 dark:text-emerald-200"
      : isBlocked
        ? "border-red-300 bg-red-100 text-red-700 dark:border-red-500/40 dark:bg-red-900/30 dark:text-red-200"
        : "border-amber-300 bg-amber-100 text-amber-700 dark:border-amber-500/40 dark:bg-amber-900/30 dark:text-amber-200";
    const statusClass = isOk
      ? "font-semibold text-emerald-600 dark:text-emerald-300"
      : isBlocked
        ? "font-semibold text-red-600 dark:text-red-300"
        : "font-semibold text-amber-600 dark:text-amber-300";
    const icon = isOk ? "✓" : isBlocked ? "⛔" : "⏳";
    const statusText = isOk ? "OK" : isBlocked ? "Bloqueado" : "Pendiente";
    return (
      <div className="flex items-center justify-between rounded-lg bg-white/80 px-3 py-2 text-xs dark:bg-navy-900/70">
        <span className="flex items-center gap-2">
          <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[12px] ${iconClass}`}>
            {icon}
          </span>
          <span>{label}</span>
        </span>
        <span className={statusClass}>{statusText}</span>
      </div>
    );
  }, []);

  const canSubmitProfileSave = React.useMemo(() => {
    if (!selectedProfile) return false;
    if (!isAmbienteSwitchRequested) return true;

    const requiredNroResol = String(emisorForm.nroResol || "").trim();
    const requiredFchResol = String(emisorForm.fchResol || "").trim();
    const currentNroResol = String(selectedProfile?.emisor?.NroResol || "").trim();
    const currentFchResol = String(selectedProfile?.emisor?.FchResol || "").trim();
    const hasResolution = Boolean(requiredNroResol && requiredFchResol);
    const hasNewResolution =
      requiredNroResol !== currentNroResol || requiredFchResol !== currentFchResol;
    const hasPassword = Boolean(String(productionGuard.authPassword || "").trim());
    const hasReadImpact = Boolean(productionGuard.confirmReadImpact);
    if (!hasReadImpact || !hasResolution || !hasNewResolution || !hasPassword) return false;

    if (isPromotionToProduction) {
      const canGoLive = Boolean(selectedProfile.is_default);
      const hasChecklist = Boolean(productionGuard.confirmReadiness);
      const hasTyped = String(productionGuard.typedConfirmation || "").trim().toUpperCase() === "PRODUCCION";
      return Boolean(canGoLive && hasChecklist && hasTyped);
    }

    if (isDemotionToCertification) {
      const hasSandboxWarning = Boolean(productionGuard.confirmSandboxMode);
      const hasTyped =
        String(productionGuard.typedConfirmation || "").trim().toUpperCase() === "CERTIFICACION";
      return Boolean(hasSandboxWarning && hasTyped);
    }

    return false;
  }, [
    selectedProfile,
    isAmbienteSwitchRequested,
    isPromotionToProduction,
    isDemotionToCertification,
    emisorForm,
    productionGuard,
  ]);

  const filteredApiKeys = React.useMemo(() => {
    const q = String(apiKeySearch || "").trim().toLowerCase();
    if (!q) return apiKeys;
    return apiKeys.filter((k) => {
      const hay = [
        k.name,
        k.key_prefix,
        Array.isArray(k.scopes) ? k.scopes.join(" ") : "",
      ]
        .map((x) => String(x || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [apiKeys, apiKeySearch]);

  const apiKeyStats = React.useMemo(() => {
    const total = apiKeys.length;
    const revoked = apiKeys.filter((k) => Boolean(k.revoked_at)).length;
    return { total, revoked, active: Math.max(0, total - revoked) };
  }, [apiKeys]);

  React.useEffect(() => {
    if (orgProfiles.length === 0) {
      if (selectedProfileId) setSelectedProfileId("");
      return;
    }
    const exists = orgProfiles.some((p) => String(p.id) === String(selectedProfileId));
    if (!exists) {
      setSelectedProfileId(String(orgProfiles[0].id));
    }
  }, [orgProfiles, selectedProfileId]);

  const loadAll = React.useCallback(
    async (preferredProfileId = "") => {
      setLoadingData(true);
      try {
        const [{ res: orgRes, data: orgData }, { res: profRes, data: profData }] =
          await Promise.all([
            organizationsApi.list(),
            emissionProfilesApi.list(),
          ]);
        if (!orgRes?.ok) {
          throw new Error(
            orgData?.error || orgData?.message || `Error ${orgRes?.status} cargando organizaciones.`,
          );
        }
        if (!profRes?.ok) {
          throw new Error(
            profData?.error || profData?.message || `Error ${profRes?.status} cargando perfiles emisores.`,
          );
        }
        const orgItems = Array.isArray(orgData?.items) ? orgData.items : [];
        const profileItems = Array.isArray(profData?.items) ? profData.items : [];
        setOrganizations(orgItems);
        setProfiles(profileItems);

        const orgUuid =
          selectedOrgUuid ||
          currentOrganization?.organization_uuid ||
          orgItems[0]?.organization_uuid ||
          "";
        setSelectedOrgUuid(String(orgUuid || ""));

        const targetOrg = orgItems.find(
          (o) => String(o.organization_uuid) === String(orgUuid),
        );
        const sameOrgProfiles = profileItems
          .filter((p) => Number(p.organization_id) === Number(targetOrg?.id))
          .sort((a, b) => Number(b.is_default) - Number(a.is_default));

        const preferredId = String(preferredProfileId || "");
        const currentSelected = String(selectedProfileId || "");
        const nextProfile =
          sameOrgProfiles.find((p) => String(p.id) === preferredId) ||
          sameOrgProfiles.find((p) => String(p.id) === currentSelected) ||
          (emission.profileUuid
            ? sameOrgProfiles.find(
                (p) => String(p.profile_uuid) === String(emission.profileUuid),
              )
            : null) ||
          sameOrgProfiles[0] ||
          null;

        setSelectedProfileId(nextProfile ? String(nextProfile.id) : "");
      } finally {
        setLoadingData(false);
      }
    },
    [
      currentOrganization?.organization_uuid,
      selectedOrgUuid,
      selectedProfileId,
      emission.profileUuid,
    ],
  );

  React.useEffect(() => {
    loadAll().catch((e) => {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "Error cargando perfil.",
      });
    });
  }, [loadAll]);

  React.useEffect(() => {
    if (!selectedOrg) return;
    setOrgForm({
      name: String(selectedOrg.name || ""),
      estado: normalizeOrgStatus(selectedOrg.estado),
    });
  }, [selectedOrg]);

  React.useEffect(() => {
    if (!selectedProfile) {
      setProfileForm({
        profile_name: "",
        nombre_firmante: "",
        company_phone: "",
        company_emails_text: "",
        estado: "active",
        ambiente: "certificacion",
        is_default: false,
      });
      setEmisorForm({
        rznSoc: "",
        giroEmis: "",
        dirOrigen: "",
        cmnaOrigen: "",
        ciudadOrigen: "",
        nroResol: "",
        fchResol: "",
      });
      setProductionGuard({
        confirmReadImpact: false,
        confirmReadiness: false,
        confirmSandboxMode: false,
        authPassword: "",
        typedConfirmation: "",
      });
      return;
    }
    setProfileForm({
      profile_name: String(selectedProfile.profile_name || ""),
      nombre_firmante: String(selectedProfile.nombre_firmante || ""),
      company_phone: String(selectedProfile.company_phone || ""),
      company_emails_text: formatCompanyEmails(selectedProfile.company_emails),
      estado: normalizeProfileStatus(selectedProfile.estado),
      ambiente: normalizeAmbiente(selectedProfile.ambiente),
      is_default: Boolean(selectedProfile.is_default),
    });
    setEmisorForm(normalizeEmisorForForm(selectedProfile));
    setProductionGuard({
      confirmReadImpact: false,
      confirmReadiness: false,
      confirmSandboxMode: false,
      authPassword: "",
      typedConfirmation: "",
    });
    setAmbienteSwitchModal({ open: false, step: 1, targetAmbiente: "certificacion" });
  }, [selectedProfile]);

  const syncEmissionStore = React.useCallback(
    async (preferredProfileUuid = undefined) => {
      const hasPreferred = preferredProfileUuid !== undefined && preferredProfileUuid !== null;
      const preferredValue = hasPreferred ? String(preferredProfileUuid || "") : "";
      await useEmissionAmbienteStore.getState().fetchForUser({
        organizationId: currentOrganization?.id,
        preferredProfileUuid: hasPreferred ? preferredValue : emission.profileUuid,
      });
      if (hasPreferred && preferredValue) {
        emission.setSelectedProfileUuid?.(preferredValue);
      }
    },
    [currentOrganization?.id, emission.profileUuid, emission.setSelectedProfileUuid],
  );

  const loadApiKeys = React.useCallback(
    async ({ includeRevoked } = {}) => {
      if (!selectedOrgUuid) {
        setApiKeys([]);
        return;
      }
      setApiKeysLoading(true);
      try {
        const finalIncludeRevoked =
          typeof includeRevoked === "boolean"
            ? includeRevoked
            : Boolean(apiKeyForm.includeRevoked);
        const { res, data } = await organizationApiKeysApi.list(selectedOrgUuid, {
          includeRevoked: finalIncludeRevoked ? "true" : "false",
        });
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        const items = Array.isArray(data?.items) ? data.items : [];
        setApiKeys(items);
      } catch (e) {
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudieron cargar las API keys.",
        });
      } finally {
        setApiKeysLoading(false);
      }
    },
    [selectedOrgUuid, apiKeyForm.includeRevoked],
  );

  React.useEffect(() => {
    if (activeTab !== "api_keys") return;
    loadApiKeys().catch(() => {});
  }, [activeTab, loadApiKeys]);

  const loadMembers = React.useCallback(async () => {
    if (!selectedOrgUuid) {
      setMembers([]);
      return;
    }
    setMembersLoading(true);
    try {
      const { res, data } = await organizationMembersApi.list(selectedOrgUuid);
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      setMembers(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudieron cargar los miembros.",
      });
    } finally {
      setMembersLoading(false);
    }
  }, [selectedOrgUuid]);

  React.useEffect(() => {
    if (activeTab !== "miembros") return;
    loadMembers().catch(() => {});
  }, [activeTab, loadMembers]);

  // Alta de un miembro.
  //
  // No crea la cuenta: la persona se registra en la aplicación y elige su
  // propia contraseña. Acá solo se le da acceso a la empresa con un rol. Esto
  // reemplaza el SQL a mano contra producción con que se daban de alta los
  // contadores.
  const [nuevoMiembroEmail, setNuevoMiembroEmail] = React.useState("");
  const [nuevoMiembroNombre, setNuevoMiembroNombre] = React.useState("");
  const [nuevoMiembroRol, setNuevoMiembroRol] = React.useState("contador");
  const [agregandoMiembro, setAgregandoMiembro] = React.useState(false);

  const handleAgregarMiembro = React.useCallback(
    async (e) => {
      e?.preventDefault?.();
      const email = nuevoMiembroEmail.trim().toLowerCase();
      if (!selectedOrgUuid || !email) return;
      setAgregandoMiembro(true);
      try {
        const { res, data } = await organizationMembersApi.add(selectedOrgUuid, {
          email,
          role: nuevoMiembroRol,
          fullName: nuevoMiembroNombre.trim(),
        });
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        setMembers((prev) => {
          const sinDuplicar = prev.filter((m) => m.id !== data.item.id);
          return [...sinDuplicar, data.item];
        });
        setNuevoMiembroEmail("");
        setNuevoMiembroNombre("");
        const rolTexto = MEMBER_ROLE_LABELS[data.item.role] || data.item.role;
        setFeedback({
          type: "success",
          text: data.item.invitado
            ? data.item.invitacion_enviada
              ? `${data.item.email} ya tiene acceso como ${rolTexto}. Le llegó un correo para crear su contraseña.`
              : `${data.item.email} ya tiene acceso como ${rolTexto}, pero el correo de invitación no salió. Pídele que entre con "Olvidé mi contraseña".`
            : `${data.item.email} ya tiene acceso como ${rolTexto}.`,
        });
      } catch (err) {
        setFeedback({
          type: "error",
          text: err instanceof Error ? err.message : "No se pudo agregar a la persona.",
        });
      } finally {
        setAgregandoMiembro(false);
      }
    },
    [selectedOrgUuid, nuevoMiembroEmail, nuevoMiembroRol, nuevoMiembroNombre],
  );

  const handleReenviarInvitacion = React.useCallback(
    async (member) => {
      if (!selectedOrgUuid || !member?.id) return;
      setMemberSavingId(member.id);
      try {
        const { res, data } = await organizationMembersApi.resendInvite(
          selectedOrgUuid,
          member.id,
        );
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        setFeedback({
          type: "success",
          text: `Le reenviamos el correo a ${member.email} para que cree su contraseña. El enlace vence en 1 hora.`,
        });
      } catch (e) {
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudo reenviar la invitación.",
        });
      } finally {
        setMemberSavingId(null);
      }
    },
    [selectedOrgUuid],
  );

  const handleCambiarAccesoMiembro = React.useCallback(
    async (member, cambios) => {
      if (!selectedOrgUuid || !member?.id) return;
      setMemberSavingId(member.id);
      try {
        const { res, data } = await organizationMembersApi.updateAccess(
          selectedOrgUuid,
          member.id,
          cambios,
        );
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, ...data.item } : m)));
      } catch (e) {
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudo cambiar el acceso.",
        });
      } finally {
        setMemberSavingId(null);
      }
    },
    [selectedOrgUuid],
  );

  const handleToggleMemberNotify = React.useCallback(
    async (member, nextValue) => {
      if (!selectedOrgUuid || !member?.id) return;
      setMemberSavingId(member.id);
      // Optimista: reflejamos el cambio y revertimos si falla.
      setMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, notify_compras: nextValue } : m)),
      );
      try {
        const { res, data } = await organizationMembersApi.updateNotify(
          selectedOrgUuid,
          member.id,
          nextValue,
        );
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        setMembers((prev) =>
          prev.map((m) => (m.id === member.id ? { ...m, ...data.item } : m)),
        );
      } catch (e) {
        setMembers((prev) =>
          prev.map((m) => (m.id === member.id ? { ...m, notify_compras: !nextValue } : m)),
        );
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudo actualizar el miembro.",
        });
      } finally {
        setMemberSavingId(null);
      }
    },
    [selectedOrgUuid],
  );

  React.useEffect(() => {
    setPlainKeyNotice("");
  }, [selectedOrgUuid]);

  const handleSaveOrganization = React.useCallback(async () => {
    if (!selectedOrgUuid) return;
    setFeedback({ type: "", text: "" });
    setSavingOrg(true);
    try {
      const { res, data } = await organizationsApi.update(selectedOrgUuid, {
        name: orgForm.name.trim(),
        estado: orgForm.estado,
      });
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      await loadAll(selectedProfileId);
      await refreshSession();
      setFeedback({ type: "success", text: "Organización actualizada correctamente." });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo actualizar la organización.",
      });
    } finally {
      setSavingOrg(false);
    }
  }, [selectedOrgUuid, orgForm, loadAll, selectedProfileId, refreshSession]);

  const handleRequestAmbienteSwitch = React.useCallback(() => {
    if (!selectedProfile) return;
    const current = normalizeAmbiente(selectedProfile.ambiente);
    const opposite = current === "produccion" ? "certificacion" : "produccion";
    setProductionGuard({
      confirmReadImpact: false,
      confirmReadiness: false,
      confirmSandboxMode: false,
      typedConfirmation: "",
      authPassword: "",
    });
    setAmbienteSwitchModal({ open: true, step: 1, targetAmbiente: opposite });
  }, [selectedProfile]);

  const handleCancelAmbienteSwitch = React.useCallback(() => {
    setProfileForm((p) => ({ ...p, ambiente: currentAmbiente }));
    setAmbienteSwitchModal((m) => ({ ...m, open: false, step: 1 }));
    setProductionGuard({
      confirmReadImpact: false,
      confirmReadiness: false,
      confirmSandboxMode: false,
      typedConfirmation: "",
      authPassword: "",
    });
  }, [currentAmbiente]);

  const handleCloseAmbienteSwitchModal = React.useCallback(() => {
    setAmbienteSwitchModal((m) => ({ ...m, open: false, step: 1 }));
    setProductionGuard({
      confirmReadImpact: false,
      confirmReadiness: false,
      confirmSandboxMode: false,
      typedConfirmation: "",
      authPassword: "",
    });
  }, []);
  const handleAttemptCloseAmbienteSwitchModal = React.useCallback(() => {
    if (hasAmbienteModalProgress) {
      const confirmed = window.confirm(
        "Tienes avances sin aplicar en este asistente. Si cierras ahora, perderás esta solicitud. ¿Deseas cerrar?",
      );
      if (!confirmed) return;
    }
    handleCloseAmbienteSwitchModal();
  }, [hasAmbienteModalProgress, handleCloseAmbienteSwitchModal]);

  const handleModalBackStep = React.useCallback(() => {
    setAmbienteSwitchModal((m) => ({
      ...m,
      step: Math.max(1, Number(m.step || 1) - 1),
    }));
  }, []);

  const handleModalNextStep = React.useCallback(() => {
    setAmbienteSwitchModal((m) => ({
      ...m,
      step: Math.min(modalTotalSteps, Number(m.step || 1) + 1),
    }));
  }, [modalTotalSteps]);

  const handleApplyAmbienteSwitchFromModal = React.useCallback(() => {
    if (!selectedProfile) return;
    if (!modalChecklist.allReady) {
      setFeedback({
        type: "error",
        text: "Aún hay validaciones pendientes en el resumen final del cambio de ambiente.",
      });
      return;
    }
    if (!productionGuard.confirmReadImpact) {
      setFeedback({ type: "error", text: "Debes leer y aceptar las consecuencias del cambio." });
      return;
    }
    if (!modalResolutionCheck.hasResolution || !modalResolutionCheck.hasNewResolution) {
      setFeedback({
        type: "error",
        text: "Debes ingresar NroResol/FchResol nuevos antes de solicitar el cambio de ambiente.",
      });
      return;
    }
    if (isModalPromotionToProduction) {
      if (!Boolean(selectedProfile.is_default)) {
        setFeedback({
          type: "error",
          text: "No se puede pasar a producción si el perfil no está marcado como default:true.",
        });
        return;
      }
      if (!productionGuard.confirmReadiness) {
        setFeedback({
          type: "error",
          text: "Debes confirmar que completaste los requisitos previos para producción.",
        });
        return;
      }
    } else if (!productionGuard.confirmSandboxMode) {
      setFeedback({
        type: "error",
        text: "Debes confirmar que entiendes el impacto de volver a certificación.",
      });
      return;
    }
    const hasPassword = Boolean(String(productionGuard.authPassword || "").trim());
    const typed = String(productionGuard.typedConfirmation || "").trim().toUpperCase();
    if (!hasPassword || typed !== modalExpectedWord) {
      setFeedback({
        type: "error",
        text: `Debes ingresar la clave y escribir "${modalExpectedWord}" para confirmar.`,
      });
      return;
    }
    setProfileForm((p) => ({ ...p, ambiente: modalTargetAmbiente }));
    setAmbienteSwitchModal((m) => ({ ...m, open: false, step: 1 }));
  }, [
    selectedProfile,
    productionGuard,
    modalResolutionCheck,
    modalChecklist.allReady,
    isModalPromotionToProduction,
    modalExpectedWord,
    modalTargetAmbiente,
  ]);

  React.useEffect(() => {
    if (!ambienteSwitchModal.open) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      handleAttemptCloseAmbienteSwitchModal();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [ambienteSwitchModal.open, handleAttemptCloseAmbienteSwitchModal]);

  const handleSaveProfile = React.useCallback(async () => {
    if (!selectedProfile) return;
    setFeedback({ type: "", text: "" });
    setSavingProfile(true);
    try {
      const nextAmbiente = normalizeAmbiente(profileForm.ambiente);

      if (isAmbienteSwitchRequested) {
        const requiredNroResol = String(emisorForm.nroResol || "").trim();
        const requiredFchResol = String(emisorForm.fchResol || "").trim();
        const currentNroResol = String(selectedProfile?.emisor?.NroResol || "").trim();
        const currentFchResol = String(selectedProfile?.emisor?.FchResol || "").trim();

        if (!requiredNroResol || !requiredFchResol) {
          throw new Error(
            "Para cambiar de ambiente debes ingresar NroResol y FchResol nuevos en el perfil emisor.",
          );
        }
        if (requiredNroResol === currentNroResol && requiredFchResol === currentFchResol) {
          throw new Error(
            "Debes actualizar NroResol y/o FchResol antes de cambiar el ambiente del perfil.",
          );
        }
        if (!productionGuard.confirmReadImpact) {
          throw new Error(
            "Debes completar la lectura de consecuencias del cambio en el modal de solicitud.",
          );
        }
        if (isPromotionToProduction && !Boolean(selectedProfile.is_default)) {
          throw new Error(
            "No se puede pasar a producción si el perfil no está marcado como default: true.",
          );
        }
        if (isPromotionToProduction && !productionGuard.confirmReadiness) {
          throw new Error(
            "Debes confirmar que completaste el set básico, boletas, guías, libros, set de simulación, intercambios DTE y muestras impresas.",
          );
        }
        if (
          isPromotionToProduction
          && String(productionGuard.typedConfirmation || "").trim().toUpperCase() !== "PRODUCCION"
        ) {
          throw new Error('Para confirmar el cambio escribe exactamente "PRODUCCION".');
        }
        if (isDemotionToCertification && !productionGuard.confirmSandboxMode) {
          throw new Error(
            "Debes confirmar que en certificación las integraciones quedan en modo prueba y no se consideran movimientos contables.",
          );
        }
        if (
          isDemotionToCertification
          && String(productionGuard.typedConfirmation || "").trim().toUpperCase() !== "CERTIFICACION"
        ) {
          throw new Error('Para confirmar el cambio escribe exactamente "CERTIFICACION".');
        }

        const password = String(productionGuard.authPassword || "");
        if (!password) {
          throw new Error("Debes ingresar la clave del usuario logueado para autorizar el cambio.");
        }
        const userEmail = String(user?.email || "").trim();
        if (!userEmail) {
          throw new Error("No se pudo resolver el email del usuario logueado para validar la clave.");
        }
        const authCheck = await authApi.login({ email: userEmail, password });
        if (!authCheck?.res?.ok || authCheck?.data?.ok === false) {
          throw new Error(
            authCheck?.data?.error ||
              authCheck?.data?.message ||
              "Clave inválida: no se autorizó el cambio a producción.",
          );
        }
      }

      const baseEmisor =
        selectedProfile.emisor && typeof selectedProfile.emisor === "object"
          ? selectedProfile.emisor
          : {};
      const nextEmisor = {
        ...baseEmisor,
        RUTEmisor: selectedProfile.rut_emisor || baseEmisor.RUTEmisor,
        RznSoc: emisorForm.rznSoc.trim(),
        GiroEmis: emisorForm.giroEmis.trim(),
        DirOrigen: emisorForm.dirOrigen.trim(),
        CmnaOrigen: emisorForm.cmnaOrigen.trim(),
        CiudadOrigen: emisorForm.ciudadOrigen.trim(),
        NroResol: emisorForm.nroResol.trim(),
        FchResol: emisorForm.fchResol.trim(),
      };
      const payload = {
        profile_name: profileForm.profile_name.trim(),
        nombre_firmante: profileForm.nombre_firmante.trim(),
        company_phone: profileForm.company_phone.trim(),
        company_emails: parseCompanyEmails(profileForm.company_emails_text),
        estado: profileForm.estado,
        ambiente: nextAmbiente,
        is_default:
          isDemotionToCertification && Boolean(selectedProfile?.is_default)
            ? false
            : Boolean(profileForm.is_default),
        emisor: nextEmisor,
      };
      const { res, data } = await emissionProfilesApi.update(selectedProfile.id, payload);
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      await loadAll(String(selectedProfile.id));
      await syncEmissionStore(selectedProfile.profile_uuid);
      if (isAmbienteSwitchRequested) {
        setProductionGuard({
          confirmReadImpact: false,
          confirmReadiness: false,
          confirmSandboxMode: false,
          authPassword: "",
          typedConfirmation: "",
        });
      }
      setFeedback({ type: "success", text: "Perfil emisor actualizado correctamente." });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo actualizar el perfil emisor.",
      });
    } finally {
      setSavingProfile(false);
    }
  }, [
    selectedProfile,
    profileForm,
    emisorForm,
    productionGuard,
    isAmbienteSwitchRequested,
    isPromotionToProduction,
    isDemotionToCertification,
    user?.email,
    loadAll,
    syncEmissionStore,
  ]);

  const handleReplaceCertificate = React.useCallback(async () => {
    if (!selectedProfile) return;
    if (!certificateForm.file) {
      setFeedback({ type: "error", text: "Debes seleccionar un archivo .p12/.pfx." });
      return;
    }
    if (!String(certificateForm.certificatePassword || "").trim()) {
      setFeedback({ type: "error", text: "Debes indicar la contraseña del certificado nuevo." });
      return;
    }
    setFeedback({ type: "", text: "" });
    setSavingCertificate(true);
    try {
      const fd = new FormData();
      fd.append("certificate", certificateForm.file);
      fd.append("certificatePassword", certificateForm.certificatePassword);
      const { res, data } = await emissionProfilesApi.uploadCertificate(
        selectedProfile.id,
        fd,
      );
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      setCertificateForm({ file: null, certificatePassword: "" });
      await loadAll(String(selectedProfile.id));
      setFeedback({
        type: "success",
        text: "Certificado reemplazado correctamente para el perfil emisor seleccionado.",
      });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo reemplazar el certificado.",
      });
    } finally {
      setSavingCertificate(false);
    }
  }, [selectedProfile, certificateForm, loadAll]);

  const handleDeleteProfile = React.useCallback(async () => {
    if (!selectedProfile) return;
    const label = selectedProfile.profile_name || selectedProfile.internal_id || selectedProfile.id;
    const ok = window.confirm(
      `¿Seguro que deseas eliminar el perfil emisor "${label}"? Esta acción no se puede deshacer.`,
    );
    if (!ok) return;
    setDeletingProfile(true);
    setFeedback({ type: "", text: "" });
    try {
      const { res, data } = await emissionProfilesApi.remove(selectedProfile.id);
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      const deletedProfileUuid = String(selectedProfile.profile_uuid || "");
      await loadAll("");
      if (deletedProfileUuid && deletedProfileUuid === String(emission.profileUuid || "")) {
        await syncEmissionStore("");
      } else {
        await syncEmissionStore(emission.profileUuid || "");
      }
      setFeedback({ type: "success", text: "Perfil emisor eliminado correctamente." });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo eliminar el perfil emisor.",
      });
    } finally {
      setDeletingProfile(false);
    }
  }, [selectedProfile, loadAll, emission.profileUuid, syncEmissionStore]);

  const handleChangePassword = React.useCallback(async () => {
    const currentPassword = String(passwordForm.currentPassword || "");
    const newPassword = String(passwordForm.newPassword || "");
    const confirm = String(passwordForm.confirmPassword || "");
    if (!currentPassword || !newPassword) {
      setFeedback({ type: "error", text: "Debes completar contraseña actual y nueva." });
      return;
    }
    if (newPassword.length < 8) {
      setFeedback({ type: "error", text: "La nueva contraseña debe tener mínimo 8 caracteres." });
      return;
    }
    if (newPassword !== confirm) {
      setFeedback({ type: "error", text: "La confirmación no coincide con la nueva contraseña." });
      return;
    }
    setSavingPassword(true);
    setFeedback({ type: "", text: "" });
    try {
      const { res, data } = await authApi.changePassword({
        currentPassword,
        newPassword,
      });
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setFeedback({ type: "success", text: "Contraseña actualizada correctamente." });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo cambiar la contraseña.",
      });
    } finally {
      setSavingPassword(false);
    }
  }, [passwordForm]);

  const toggleScope = React.useCallback((scope) => {
    setApiKeyForm((prev) => {
      const has = prev.selectedScopes.includes(scope);
      let nextScopes = has
        ? prev.selectedScopes.filter((s) => s !== scope)
        : [...prev.selectedScopes, scope];
      if (scope === "*" && !has) {
        nextScopes = ["*"];
      } else if (scope !== "*" && !has) {
        nextScopes = nextScopes.filter((s) => s !== "*");
      }
      if (!nextScopes.length) nextScopes = ["read_documents"];
      return { ...prev, selectedScopes: nextScopes };
    });
  }, []);

  const handleCreateApiKey = React.useCallback(async () => {
    if (!selectedOrgUuid) return;
    const name = String(apiKeyForm.name || "").trim();
    if (!name) {
      setFeedback({ type: "error", text: "El nombre de la API key es obligatorio." });
      return;
    }
    if (!Array.isArray(apiKeyForm.selectedScopes) || !apiKeyForm.selectedScopes.length) {
      setFeedback({ type: "error", text: "Debes seleccionar al menos un scope." });
      return;
    }
    setSavingApiKey(true);
    setFeedback({ type: "", text: "" });
    setPlainKeyNotice("");
    try {
      const { res, data } = await organizationApiKeysApi.create(selectedOrgUuid, {
        name,
        scopes: apiKeyForm.selectedScopes,
        expiresAt: apiKeyForm.expiresAt ? `${apiKeyForm.expiresAt}T23:59:59.000Z` : null,
      });
      if (!res?.ok || data?.ok === false) {
        throw new Error(data?.error || data?.message || `Error ${res?.status}`);
      }
      const plainKey = String(data?.plainKey || "").trim();
      if (plainKey) {
        setPlainKeyNotice(plainKey);
      }
      setApiKeyForm((prev) => ({
        ...prev,
        name: "",
        expiresAt: "",
        selectedScopes: ["read_documents"],
      }));
      await loadApiKeys();
      setFeedback({ type: "success", text: "API key creada correctamente." });
    } catch (e) {
      setFeedback({
        type: "error",
        text: e instanceof Error ? e.message : "No se pudo crear la API key.",
      });
    } finally {
      setSavingApiKey(false);
    }
  }, [selectedOrgUuid, apiKeyForm, loadApiKeys]);

  const handleRegenerateApiKey = React.useCallback(
    async (apiKeyUuid) => {
      if (!selectedOrgUuid || !apiKeyUuid) return;
      if (!window.confirm("Se regenerará el secreto y el anterior dejará de funcionar. ¿Continuar?")) {
        return;
      }
      setApiKeysBusyById((p) => ({ ...p, [apiKeyUuid]: true }));
      setFeedback({ type: "", text: "" });
      setPlainKeyNotice("");
      try {
        const { res, data } = await organizationApiKeysApi.regenerate(
          selectedOrgUuid,
          apiKeyUuid,
        );
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        const plainKey = String(data?.plainKey || "").trim();
        if (plainKey) {
          setPlainKeyNotice(plainKey);
        }
        await loadApiKeys();
        setFeedback({ type: "success", text: "API key regenerada correctamente." });
      } catch (e) {
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudo regenerar la API key.",
        });
      } finally {
        setApiKeysBusyById((p) => {
          const next = { ...p };
          delete next[apiKeyUuid];
          return next;
        });
      }
    },
    [selectedOrgUuid, loadApiKeys],
  );

  const handleRevokeApiKey = React.useCallback(
    async (apiKeyUuid) => {
      if (!selectedOrgUuid || !apiKeyUuid) return;
      if (!window.confirm("Se revocará esta API key y no podrá volver a usarse. ¿Continuar?")) {
        return;
      }
      setApiKeysBusyById((p) => ({ ...p, [apiKeyUuid]: true }));
      setFeedback({ type: "", text: "" });
      try {
        const { res, data } = await organizationApiKeysApi.revoke(
          selectedOrgUuid,
          apiKeyUuid,
        );
        if (!res?.ok || data?.ok === false) {
          throw new Error(data?.error || data?.message || `Error ${res?.status}`);
        }
        await loadApiKeys();
        setFeedback({ type: "success", text: "API key revocada correctamente." });
      } catch (e) {
        setFeedback({
          type: "error",
          text: e instanceof Error ? e.message : "No se pudo revocar la API key.",
        });
      } finally {
        setApiKeysBusyById((p) => {
          const next = { ...p };
          delete next[apiKeyUuid];
          return next;
        });
      }
    },
    [selectedOrgUuid, loadApiKeys],
  );

  return (
    <div className="flex w-full flex-col gap-5">
      <Card extra="w-full p-6">
        <h2 className="text-xl font-bold text-navy-700 dark:text-white">Configuración de perfil</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
          Edita tu cuenta, tus organizaciones y el perfil emisor (resolución, dirección, certificado).
        </p>
        {feedback.text ? (
          <p
            className={`mt-4 rounded-lg border px-4 py-3 text-sm ${
              feedback.type === "success"
                ? "border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-100"
                : "border-red-300 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-950/30 dark:text-red-100"
            }`}
            role={feedback.type === "error" ? "alert" : "status"}
          >
            {feedback.text}
          </p>
        ) : null}
        <div className="mt-4 inline-flex rounded-2xl border border-gray-200 bg-lightPrimary/40 p-1 dark:border-white/10 dark:bg-navy-900/40">
          {PROFILE_TABS.filter((tab) => !(
            isReadOnly
            // Integraciones es configuración del dueño: sus endpoints exigen
            // ser dueño de la organización, así que a un contador le saldría
            // una pestaña que solo puede mirar romperse.
            && (tab.id === "miembros" || tab.id === "api_keys" || tab.id === "integraciones")
          )).map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                  active
                    ? "bg-white text-navy-700 shadow-sm dark:bg-brand-400 dark:text-white"
                    : "text-gray-600 hover:bg-white/70 dark:text-gray-300 dark:hover:bg-navy-800"
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </Card>
      {activeTab === "perfil" ? (
        <>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <Card extra="w-full p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Cuenta logueada</h3>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase text-gray-500">Nombre</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {user?.full_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500">Email</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {user?.email || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500">Usuario UUID</p>
              <p className="break-all text-sm font-medium text-navy-700 dark:text-white">
                {auth?.user_uuid || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase text-gray-500">Organización actual</p>
              <p className="text-sm font-medium text-navy-700 dark:text-white">
                {currentOrganization?.name || currentOrganization?.internal_id || "—"}
              </p>
            </div>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-4 dark:border-white/10">
            <h4 className="text-sm font-semibold text-navy-700 dark:text-white">Cambiar contraseña</h4>
            <div className="mt-3 grid grid-cols-1 gap-3">
              <input
                type="password"
                placeholder="Contraseña actual"
                value={passwordForm.currentPassword}
                onChange={(e) =>
                  setPasswordForm((p) => ({ ...p, currentPassword: e.target.value }))
                }
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              />
              <input
                type="password"
                placeholder="Nueva contraseña"
                value={passwordForm.newPassword}
                onChange={(e) =>
                  setPasswordForm((p) => ({ ...p, newPassword: e.target.value }))
                }
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              />
              <input
                type="password"
                placeholder="Confirmar nueva contraseña"
                value={passwordForm.confirmPassword}
                onChange={(e) =>
                  setPasswordForm((p) => ({ ...p, confirmPassword: e.target.value }))
                }
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleChangePassword}
                  disabled={savingPassword}
                  className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
                >
                  {savingPassword ? "Guardando…" : "Actualizar contraseña"}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card extra="w-full p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Organización</h3>
          <div className="mt-4 grid grid-cols-1 gap-3">
            <label className="text-xs font-medium uppercase text-gray-500">
              Organización
              <select
                value={selectedOrgUuid}
                onChange={(e) => setSelectedOrgUuid(e.target.value)}
                disabled={loadingData}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm normal-case text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              >
                {organizations.map((o) => (
                  <option key={o.organization_uuid} value={o.organization_uuid}>
                    {(o.name || o.internal_id || o.organization_uuid) + ` (${o.estado})`}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-medium uppercase text-gray-500">
              Nombre organización
              <input
                type="text"
                value={orgForm.name}
                onChange={(e) => setOrgForm((p) => ({ ...p, name: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm normal-case text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              />
            </label>
            <label className="text-xs font-medium uppercase text-gray-500">
              Estado organización
              <select
                value={orgForm.estado}
                onChange={(e) => setOrgForm((p) => ({ ...p, estado: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm normal-case text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white"
              >
                {ORG_STATUS_OPTIONS.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleSaveOrganization}
                disabled={savingOrg || !selectedOrg}
                className="rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
              >
                {savingOrg ? "Guardando…" : "Guardar organización"}
              </button>
            </div>
          </div>
        </Card>
      </div>
        </>
      ) : null}
      {activeTab === "perfiles_emision" ? (
        <Card extra="w-full p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">Perfiles de Emisión</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Administra perfiles emisores de la organización: listar, editar y eliminar.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-12">
            <div className="xl:col-span-4">
              <div className={SURFACE_PANEL}>
                <label className={LABEL_BASE}>
                  Organización
                  <select
                    value={selectedOrgUuid}
                    onChange={(e) => setSelectedOrgUuid(e.target.value)}
                    disabled={loadingData}
                    className={`${INPUT_BASE} mt-1 normal-case`}
                  >
                    {organizations.map((o) => (
                      <option key={o.organization_uuid} value={o.organization_uuid}>
                        {(o.name || o.internal_id || o.organization_uuid) + ` (${o.estado})`}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="mt-3 overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-navy-900/60">
                        <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Perfil</th>
                        <th className="px-3 py-2 text-left text-[11px] uppercase text-gray-500">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgProfiles.length === 0 ? (
                        <tr>
                          <td
                            colSpan={2}
                            className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400"
                          >
                            No hay perfiles para esta organización.
                          </td>
                        </tr>
                      ) : (
                        orgProfiles.map((p) => {
                          const active = String(selectedProfileId) === String(p.id);
                          return (
                            <tr
                              key={p.id}
                              className={`cursor-pointer border-t border-gray-100 dark:border-white/5 ${
                                active ? "bg-brand-50 dark:bg-brand-500/10" : "hover:bg-gray-50 dark:hover:bg-navy-800/70"
                              }`}
                              onClick={() => setSelectedProfileId(String(p.id))}
                            >
                              <td className="px-3 py-2 text-xs text-navy-700 dark:text-white">
                                <p className="font-semibold">{p.profile_name || p.internal_id || `Perfil ${p.id}`}</p>
                                <p className="text-[11px] text-gray-500 dark:text-gray-400">{p.rut_emisor} · {p.ambiente}</p>
                              </td>
                              <td className="px-3 py-2 text-xs">
                                <span
                                  className={`rounded-full px-2 py-1 ${
                                    String(p.estado) === "active"
                                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                                      : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                                  }`}
                                >
                                  {String(p.estado) === "active" ? "Activo" : "Inactivo"}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="xl:col-span-8">
              <div className={SURFACE_PANEL}>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <label className={LABEL_BASE}>
                    Perfil emisor
                    <select
                      value={selectedProfileId}
                      onChange={(e) => setSelectedProfileId(e.target.value)}
                      disabled={loadingData || orgProfiles.length === 0}
                      className={`${INPUT_BASE} mt-1 normal-case`}
                    >
                      {orgProfiles.length === 0 ? (
                        <option value="">Sin perfiles para esta organización</option>
                      ) : (
                        orgProfiles.map((p) => (
                          <option key={p.id} value={p.id}>
                            {(p.profile_name || p.internal_id || `Perfil ${p.id}`) +
                              ` · ${p.rut_emisor} · ${p.ambiente}`}
                          </option>
                        ))
                      )}
                    </select>
                  </label>
                  <div className={LABEL_BASE}>
                    Identificadores (solo lectura)
                    <div className="mt-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm normal-case text-navy-700 dark:border-white/15 dark:bg-navy-900 dark:text-white">
                      <div>ID: {selectedProfile?.id ?? "—"}</div>
                      <div>UUID: {selectedProfile?.profile_uuid || "—"}</div>
                      <div>Internal ID: {selectedProfile?.internal_id || "—"}</div>
                      <div>RUT emisor: {selectedProfile?.rut_emisor || "—"}</div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <input
                    type="text"
                    placeholder="Nombre del perfil"
                    value={profileForm.profile_name}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, profile_name: e.target.value }))
                    }
                    className={INPUT_BASE}
                  />
                  <input
                    type="text"
                    placeholder="Nombre firmante"
                    value={profileForm.nombre_firmante}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, nombre_firmante: e.target.value }))
                    }
                    className={INPUT_BASE}
                  />
                  <input
                    type="text"
                    placeholder="Teléfono empresa"
                    value={profileForm.company_phone}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, company_phone: e.target.value }))
                    }
                    className={INPUT_BASE}
                  />
                  <select
                    value={profileForm.estado}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, estado: e.target.value }))
                    }
                    className={INPUT_BASE}
                  >
                    {PROFILE_STATUS_OPTIONS.map((x) => (
                      <option key={x.value} value={x.value}>
                        Estado perfil: {x.label}
                      </option>
                    ))}
                  </select>
                  <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 dark:border-white/15 dark:bg-navy-900">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300">
                      Ambiente actual
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-navy-700 dark:text-white">
                      {currentAmbiente === "produccion" ? "Producción" : "Certificación"}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleRequestAmbienteSwitch}
                        disabled={!selectedProfile}
                        className="rounded-xl border-2 border-brand-500 bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-brand-600 hover:border-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:border-brand-400 dark:bg-brand-400 dark:hover:bg-brand-300"
                      >
                        {switchButtonLabel}
                      </button>
                      {isAmbienteSwitchRequested ? (
                        <button
                          type="button"
                          onClick={handleCancelAmbienteSwitch}
                          className="rounded-xl border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-navy-700 transition hover:bg-gray-50 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                        >
                          Cancelar cambio de ambiente
                        </button>
                      ) : null}
                    </div>
                    {isAmbienteSwitchRequested ? (
                      <p className="mt-2 text-xs text-brand-600 dark:text-brand-300">
                        Cambio pendiente: {ambienteTargetLabel}
                      </p>
                    ) : null}
                  </div>
                  <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white">
                    <input
                      type="checkbox"
                      checked={Boolean(profileForm.is_default)}
                      onChange={(e) =>
                        setProfileForm((p) => ({ ...p, is_default: e.target.checked }))
                      }
                    />
                    Marcar como perfil por defecto
                  </label>
                  <textarea
                    rows={4}
                    placeholder="Correos empresa (uno por línea o separados por coma)"
                    value={profileForm.company_emails_text}
                    onChange={(e) =>
                      setProfileForm((p) => ({ ...p, company_emails_text: e.target.value }))
                    }
                    className={`md:col-span-2 xl:col-span-3 ${INPUT_BASE}`}
                  />
                </div>

                <div className="mt-5 border-t border-gray-200 pt-4 dark:border-white/10">
                  <h4 className="text-sm font-semibold text-navy-700 dark:text-white">Bloque emisor (SII)</h4>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <input
                      type="text"
                      placeholder="Razón social emisor (RznSoc)"
                      value={emisorForm.rznSoc}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, rznSoc: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="Giro emisor (GiroEmis)"
                      value={emisorForm.giroEmis}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, giroEmis: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="Dirección facturación (DirOrigen)"
                      value={emisorForm.dirOrigen}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, dirOrigen: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="Comuna (CmnaOrigen)"
                      value={emisorForm.cmnaOrigen}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, cmnaOrigen: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="Ciudad (CiudadOrigen)"
                      value={emisorForm.ciudadOrigen}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, ciudadOrigen: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="N° Resolución (NroResol)"
                      value={emisorForm.nroResol}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, nroResol: e.target.value }))
                      }
                      className={INPUT_BASE}
                    />
                    <input
                      type="text"
                      placeholder="Fecha resolución YYYY-MM-DD (FchResol)"
                      value={emisorForm.fchResol}
                      onChange={(e) =>
                        setEmisorForm((p) => ({ ...p, fchResol: e.target.value }))
                      }
                      className={`md:col-span-2 xl:col-span-3 ${INPUT_BASE}`}
                    />
                  </div>
                  {isAmbienteSwitchRequested ? (
                    <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50/90 p-4 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
                      <p className="font-semibold">Cambio pendiente a {ambienteTargetLabel}</p>
                      <p className="mt-1 text-xs opacity-90">
                        Solicitud configurada con modal paso a paso. Para aplicar definitivamente, usa
                        &quot;Guardar perfil emisor&quot;.
                      </p>
                    </div>
                  ) : null}
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleDeleteProfile}
                      disabled={deletingProfile || !selectedProfile}
                      className="rounded-xl border border-red-400 bg-white px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-400 dark:bg-navy-900 dark:text-red-300 dark:hover:bg-navy-800"
                    >
                      {deletingProfile ? "Eliminando…" : "Eliminar perfil"}
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveProfile}
                      disabled={savingProfile || !canSubmitProfileSave}
                      className={BTN_PRIMARY}
                      title={
                        isAmbienteSwitchRequested && !canSubmitProfileSave
                          ? "Completa confirmación, clave y datos de resolución para autorizar el cambio de ambiente."
                          : "Guardar cambios del perfil emisor."
                      }
                    >
                      {savingProfile ? "Guardando…" : "Guardar perfil emisor"}
                    </button>
                  </div>
                </div>

                <div className="mt-5 border-t border-gray-200 pt-4 dark:border-white/10">
                  <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
                    Reemplazar certificado del emisor
                  </h4>
                  <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                    <input
                      type="file"
                      accept=".p12,.pfx"
                      onChange={(e) =>
                        setCertificateForm((p) => ({
                          ...p,
                          file: e.target.files?.[0] || null,
                        }))
                      }
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 file:mr-3 file:rounded-md file:border-0 file:bg-brand-500 file:px-3 file:py-1 file:text-white dark:border-white/15 dark:bg-navy-900 dark:text-white dark:file:bg-brand-400"
                    />
                    <input
                      type="password"
                      placeholder="Contraseña del certificado nuevo"
                      value={certificateForm.certificatePassword}
                      onChange={(e) =>
                        setCertificateForm((p) => ({
                          ...p,
                          certificatePassword: e.target.value,
                        }))
                      }
                      className={INPUT_BASE}
                    />
                  </div>
                  <div className="mt-4 flex flex-wrap justify-end gap-2">
                    {!isReadOnly ? (
                      <button
                        type="button"
                        onClick={() => setCertDownloadOpen(true)}
                        disabled={!selectedProfile}
                        className={BTN_SECONDARY}
                        title="Descargar el certificado digital (requiere verificación por correo)"
                      >
                        Descargar certificado
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={handleReplaceCertificate}
                      disabled={savingCertificate || !selectedProfile}
                      className={BTN_SECONDARY}
                    >
                      {savingCertificate ? "Reemplazando…" : "Reemplazar certificado"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      ) : null}
      {!isReadOnly ? (
        <CertificateDownloadModal
          isOpen={certDownloadOpen}
          onClose={() => setCertDownloadOpen(false)}
          profile={selectedProfile}
        />
      ) : null}
      {activeTab === "miembros" ? (
        <Card extra="w-full p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Miembros de la organización
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Activa a quién le llegan los <strong>avisos de compras</strong> (correo por
            cada DTE recibido). Solo tú, como administrador, puedes cambiarlo.
          </p>

          {/* Alta. Antes esto era SQL a mano contra la base de producción. */}
          <form
            onSubmit={handleAgregarMiembro}
            className="mt-5 flex flex-col gap-3 rounded-xl border border-gray-200 bg-lightPrimary/40 p-4 dark:border-white/10 dark:bg-navy-900/30 sm:flex-row sm:items-end"
          >
            <label className="flex flex-1 flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              Correo de la persona
              <input
                type="email"
                required
                value={nuevoMiembroEmail}
                onChange={(e) => setNuevoMiembroEmail(e.target.value)}
                placeholder="nombre@empresa.cl"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              Nombre <span className="font-normal text-gray-400">(opcional)</span>
              <input
                type="text"
                value={nuevoMiembroNombre}
                onChange={(e) => setNuevoMiembroNombre(e.target.value)}
                placeholder="Francisca Opazo"
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-medium text-gray-600 dark:text-gray-300">
              Rol
              <select
                value={nuevoMiembroRol}
                onChange={(e) => setNuevoMiembroRol(e.target.value)}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-800 dark:text-white"
              >
                {MEMBER_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
            <button
              type="submit"
              disabled={agregandoMiembro || !nuevoMiembroEmail.trim()}
              className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
            >
              {agregandoMiembro ? "Agregando…" : "Agregar"}
            </button>
          </form>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {MEMBER_ROLE_OPTIONS.find((o) => o.value === nuevoMiembroRol)?.hint}{" "}
            Si todavía no tiene cuenta, se la creamos y le llega un correo para que
            elija su contraseña. Nadie más la conoce.
          </p>

          {membersLoading ? (
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">Cargando miembros…</p>
          ) : members.length === 0 ? (
            <p className="mt-6 text-sm text-gray-500 dark:text-gray-400">
              No hay miembros en esta organización.
            </p>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
              <table className="w-full min-w-[560px] border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-white text-left dark:border-white/10 dark:bg-navy-900">
                    <th className="px-4 py-3 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">Usuario</th>
                    <th className="px-4 py-3 text-xs font-bold uppercase text-gray-600 dark:text-gray-300">Rol</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-300">Avisos de compras</th>
                    <th className="px-4 py-3 text-right text-xs font-bold uppercase text-gray-600 dark:text-gray-300">Acceso</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const saving = memberSavingId === m.id;
                    const on = Boolean(m.notify_compras);
                    return (
                      <tr
                        key={m.id}
                        className="border-b border-gray-100 last:border-0 dark:border-white/5"
                      >
                        <td className="px-4 py-3">
                          <div className="text-sm font-medium text-navy-800 dark:text-white">
                            {m.full_name || "—"}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{m.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-navy-700 dark:text-gray-200">
                          {/* El dueño no se edita: degradarlo dejaría la
                              empresa sin quien la administre. El backend lo
                              rechaza igual. */}
                          {m.role === "owner" ? (
                            MEMBER_ROLE_LABELS.owner
                          ) : (
                            <select
                              value={m.role}
                              disabled={saving}
                              onChange={(e) =>
                                handleCambiarAccesoMiembro(m, { role: e.target.value })
                              }
                              className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-navy-800 disabled:opacity-60 dark:border-white/15 dark:bg-navy-800 dark:text-white"
                            >
                              {MEMBER_ROLE_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          )}
                          {m.estado === "disabled" ? (
                            <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-bold uppercase text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                              sin acceso
                            </span>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            type="button"
                            role="switch"
                            aria-checked={on}
                            disabled={saving}
                            onClick={() => handleToggleMemberNotify(m, !on)}
                            title={on ? "Recibe avisos de compras" : "No recibe avisos de compras"}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-60 ${
                              on ? "bg-brand-500 dark:bg-brand-400" : "bg-gray-300 dark:bg-navy-700"
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition ${
                                on ? "translate-x-6" : "translate-x-1"
                              }`}
                            />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {/* El dueño no necesita invitación ni se le quita
                              el acceso: es su empresa. */}
                          {m.role === "owner" ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <div className="flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleReenviarInvitacion(m)}
                                title="Le reenvía el correo con el enlace para crear su contraseña. Sirve si se le perdió o se le venció."
                                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-navy-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-white/20 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                              >
                                Reenviar invitación
                              </button>
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() =>
                                  handleCambiarAccesoMiembro(m, {
                                    estado: m.estado === "disabled" ? "active" : "disabled",
                                  })
                                }
                                title={
                                  m.estado === "disabled"
                                    ? "Le devuelve el acceso a la empresa."
                                    : "Le quita el acceso sin borrar nada. Reversible."
                                }
                                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-navy-700 transition hover:bg-gray-50 disabled:opacity-60 dark:border-white/20 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                              >
                                {m.estado === "disabled" ? "Dar acceso" : "Quitar acceso"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ) : null}
      {activeTab === "api_keys" ? (
        <Card extra="w-full p-6">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            API Keys por organización
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            Crea, regenera y revoca claves para integraciones. El secreto en texto plano se muestra una sola vez.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm dark:border-emerald-500/30 dark:bg-emerald-950/30">
              <p className="text-[11px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">Activas</p>
              <p className="text-lg font-bold text-emerald-800 dark:text-emerald-100">{apiKeyStats.active}</p>
            </div>
            <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm dark:border-red-500/30 dark:bg-red-950/30">
              <p className="text-[11px] uppercase tracking-wide text-red-700 dark:text-red-300">Revocadas</p>
              <p className="text-lg font-bold text-red-800 dark:text-red-100">{apiKeyStats.revoked}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-900/60">
              <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-300">Total</p>
              <p className="text-lg font-bold text-navy-700 dark:text-white">{apiKeyStats.total}</p>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <label className={LABEL_BASE}>
              Organización
              <select
                value={selectedOrgUuid}
                onChange={(e) => setSelectedOrgUuid(e.target.value)}
                disabled={loadingData}
                className={`${INPUT_BASE} mt-1 normal-case`}
              >
                {organizations.map((o) => (
                  <option key={o.organization_uuid} value={o.organization_uuid}>
                    {(o.name || o.internal_id || o.organization_uuid) + ` (${o.estado})`}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-800 dark:border-white/15 dark:bg-navy-900 dark:text-white">
              <input
                type="checkbox"
                checked={Boolean(apiKeyForm.includeRevoked)}
                onChange={(e) =>
                  setApiKeyForm((p) => ({ ...p, includeRevoked: e.target.checked }))
                }
              />
              Mostrar revocadas
            </label>
          </div>

          <div className={`${SURFACE_PANEL} mt-5`}>
            <h4 className="text-sm font-semibold text-navy-700 dark:text-white">
              Crear nueva API key
            </h4>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <input
                type="text"
                placeholder="Nombre de la API key"
                value={apiKeyForm.name}
                onChange={(e) => setApiKeyForm((p) => ({ ...p, name: e.target.value }))}
                className={INPUT_BASE}
              />
              <input
                type="date"
                value={apiKeyForm.expiresAt}
                onChange={(e) =>
                  setApiKeyForm((p) => ({ ...p, expiresAt: e.target.value }))
                }
                className={INPUT_BASE}
              />
            </div>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Scopes permitidos para la key:
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {API_KEY_SCOPE_OPTIONS.map((scope) => {
                const selected = apiKeyForm.selectedScopes.includes(scope.value);
                return (
                  <button
                    key={scope.value}
                    type="button"
                    onClick={() => toggleScope(scope.value)}
                    className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                      selected
                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:border-brand-400 dark:bg-brand-500/20 dark:text-brand-200"
                        : "border-gray-200 bg-white text-navy-700 hover:bg-gray-50 dark:border-white/15 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
                    }`}
                  >
                    {scope.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => loadApiKeys()}
                disabled={apiKeysLoading}
                className={BTN_SECONDARY}
              >
                {apiKeysLoading ? "Actualizando…" : "Actualizar lista"}
              </button>
              <button
                type="button"
                onClick={handleCreateApiKey}
                disabled={savingApiKey || !selectedOrgUuid}
                className={BTN_PRIMARY}
              >
                {savingApiKey ? "Creando…" : "Crear API key"}
              </button>
            </div>
          </div>

          {plainKeyNotice ? (
            <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
              <p className="font-semibold">
                Copia esta clave ahora (no se mostrará de nuevo):
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <code className="rounded bg-white px-2 py-1 text-xs dark:bg-navy-900">
                  {plainKeyNotice}
                </code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(plainKeyNotice);
                      setFeedback({ type: "success", text: "API key copiada al portapapeles." });
                    } catch {
                      setFeedback({ type: "error", text: "No se pudo copiar la API key." });
                    }
                  }}
                  className="rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 dark:border-amber-400 dark:bg-navy-900 dark:text-amber-200 dark:hover:bg-navy-800"
                >
                  Copiar
                </button>
              </div>
            </div>
          ) : null}

          <div className="mt-4">
            <input
              type="search"
              placeholder="Buscar por nombre, prefix o scope..."
              value={apiKeySearch}
              onChange={(e) => setApiKeySearch(e.target.value)}
              className={INPUT_BASE}
            />
          </div>

          <div className="mt-5 overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10">
            <table className="w-full min-w-[860px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 dark:border-white/10 dark:bg-navy-900/60">
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Nombre</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Prefix</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Scopes</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Expira</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Estado</th>
                  <th className="px-3 py-2 text-left text-xs uppercase text-gray-500">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredApiKeys.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      {apiKeysLoading
                        ? "Cargando API keys..."
                        : "No hay API keys para esta búsqueda/organización."}
                    </td>
                  </tr>
                ) : (
                  filteredApiKeys.map((k) => {
                    const busy = Boolean(apiKeysBusyById[k.api_key_uuid]);
                    const revoked = Boolean(k.revoked_at);
                    return (
                      <tr
                        key={k.api_key_uuid}
                        className="border-b border-gray-100 dark:border-white/5"
                      >
                        <td className="px-3 py-2 text-sm text-navy-700 dark:text-white">
                          {k.name || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs text-navy-700 dark:text-white">
                          {k.key_prefix || "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-navy-700 dark:text-white">
                          {Array.isArray(k.scopes) ? k.scopes.join(", ") : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-navy-700 dark:text-white">
                          {formatDateTime(k.expires_at)}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {revoked ? (
                            <span className="rounded-full bg-red-100 px-2 py-1 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                              Revocada
                            </span>
                          ) : (
                            <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                              Activa
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleRegenerateApiKey(k.api_key_uuid)}
                              disabled={busy || revoked}
                              className="rounded-lg border border-tertiary-500 bg-white px-2 py-1 font-semibold text-tertiary-700 hover:bg-tertiary-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-tertiary-400 dark:bg-navy-900 dark:text-tertiary-300 dark:hover:bg-navy-800"
                            >
                              Regenerar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRevokeApiKey(k.api_key_uuid)}
                              disabled={busy || revoked}
                              className={BTN_DANGER}
                            >
                              Revocar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
      {ambienteSwitchModal.open ? (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 backdrop-blur-[2px] px-4 py-6"
          onClick={handleAttemptCloseAmbienteSwitchModal}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-navy-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-brand-600 dark:text-brand-300">
                  Cambio de ambiente
                </p>
                <h4 className="text-lg font-bold text-navy-700 dark:text-white">
                  Paso {modalStep} de {modalTotalSteps}: {modalTargetLabel}
                </h4>
              </div>
              <button
                type="button"
                onClick={handleAttemptCloseAmbienteSwitchModal}
                className="rounded-lg border border-gray-300 bg-white px-2.5 py-1 text-xs font-semibold text-navy-700 hover:bg-gray-50 dark:border-white/20 dark:bg-navy-900 dark:text-white dark:hover:bg-navy-800"
              >
                Cerrar
              </button>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
              <div
                className="h-full rounded-full bg-brand-500 transition-all dark:bg-brand-400"
                style={{ width: `${(modalStep / modalTotalSteps) * 100}%` }}
              />
            </div>

            {modalStep === 1 ? (
              <div className="mt-4 space-y-2 text-sm text-navy-700 dark:text-white">
                <p className="font-semibold">Lee y confirma las consecuencias del cambio</p>
                {isModalPromotionToProduction ? (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Pasar a producción habilita emisión real con efectos contables y tributarios. Verifica set
                    básico, boletas, guías, libros, set de simulación, intercambios DTE y muestras impresas.
                  </p>
                ) : (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Volver a certificación deja integraciones en modo prueba y los documentos no cuentan como
                    movimientos contables de producción.
                  </p>
                )}
                <label className="mt-3 flex items-start gap-2 rounded-xl border border-gray-200 bg-lightPrimary/30 px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800/40">
                  <input
                    type="checkbox"
                    checked={Boolean(productionGuard.confirmReadImpact)}
                    onChange={(e) =>
                      setProductionGuard((p) => ({
                        ...p,
                        confirmReadImpact: e.target.checked,
                      }))
                    }
                  />
                  <span>Entiendo el impacto y deseo continuar con este cambio de ambiente.</span>
                </label>
              </div>
            ) : null}

            {modalStep === 2 ? (
              <div className="mt-4 space-y-3 text-sm text-navy-700 dark:text-white">
                <p className="font-semibold">Validaciones previas obligatorias</p>
                <div className="rounded-xl border border-gray-200 bg-lightPrimary/30 px-3 py-2 text-xs dark:border-white/15 dark:bg-navy-800/40">
                  <p>
                    NroResol/FchResol actualizados:{" "}
                    {modalResolutionCheck.hasResolution && modalResolutionCheck.hasNewResolution ? "OK" : "Pendiente"}
                  </p>
                  {!modalResolutionCheck.hasResolution ? (
                    <p className="mt-1 text-red-600 dark:text-red-300">
                      Completa NroResol y FchResol en el formulario del perfil.
                    </p>
                  ) : null}
                  {modalResolutionCheck.hasResolution && !modalResolutionCheck.hasNewResolution ? (
                    <p className="mt-1 text-red-600 dark:text-red-300">
                      Debes cambiar NroResol y/o FchResol respecto al valor actual.
                    </p>
                  ) : null}
                </div>
                {isModalPromotionToProduction ? (
                  <>
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                      Requisito adicional: este perfil debe estar en `default:true` para poder pasar a producción.
                    </p>
                    {!Boolean(selectedProfile?.is_default) ? (
                      <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 dark:border-red-500/40 dark:bg-red-950/30 dark:text-red-200">
                        Bloqueado: el perfil seleccionado no está en default:true.
                      </p>
                    ) : null}
                    <label className="flex items-start gap-2 rounded-xl border border-gray-200 bg-lightPrimary/30 px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800/40">
                      <input
                        type="checkbox"
                        checked={Boolean(productionGuard.confirmReadiness)}
                        onChange={(e) =>
                          setProductionGuard((p) => ({
                            ...p,
                            confirmReadiness: e.target.checked,
                          }))
                        }
                      />
                      <span>Confirmo que completé set básico, boletas, guías, libros, simulación e intercambios.</span>
                    </label>
                  </>
                ) : (
                  <label className="flex items-start gap-2 rounded-xl border border-gray-200 bg-lightPrimary/30 px-3 py-2 text-sm dark:border-white/15 dark:bg-navy-800/40">
                    <input
                      type="checkbox"
                      checked={Boolean(productionGuard.confirmSandboxMode)}
                      onChange={(e) =>
                        setProductionGuard((p) => ({
                          ...p,
                          confirmSandboxMode: e.target.checked,
                        }))
                      }
                    />
                    <span>
                      Confirmo que al pasar a certificación quedará en pruebas y, si estaba default:true, guardará
                      default:false.
                    </span>
                  </label>
                )}
              </div>
            ) : null}

            {modalStep === 3 ? (
              <div className="mt-4 space-y-3 text-sm text-navy-700 dark:text-white">
                <p className="font-semibold">Autorización final</p>
                <input
                  type="password"
                  placeholder="Clave del usuario logueado"
                  value={productionGuard.authPassword}
                  onChange={(e) =>
                    setProductionGuard((p) => ({
                      ...p,
                      authPassword: e.target.value,
                    }))
                  }
                  className={INPUT_BASE}
                />
                <input
                  type="text"
                  placeholder={`Escribe "${modalExpectedWord}" para confirmar`}
                  value={productionGuard.typedConfirmation}
                  onChange={(e) =>
                    setProductionGuard((p) => ({
                      ...p,
                      typedConfirmation: e.target.value,
                    }))
                  }
                  className={`${INPUT_BASE} font-semibold tracking-wide`}
                />
              </div>
            ) : null}
            {modalStep === 4 ? (
              <div className="mt-4 space-y-3 text-sm text-navy-700 dark:text-white">
                <p className="font-semibold">Resumen final antes de aplicar</p>
                <div className="space-y-2 rounded-xl border border-gray-200 bg-lightPrimary/30 p-3 dark:border-white/15 dark:bg-navy-800/40">
                  {renderModalChecklistItem(
                    "Lectura de consecuencias",
                    modalChecklist.readImpact ? "ok" : "pending",
                  )}
                  {renderModalChecklistItem(
                    "NroResol/FchResol actualizados",
                    modalChecklist.resolutionReady ? "ok" : "pending",
                  )}
                  {isModalPromotionToProduction ? (
                    renderModalChecklistItem(
                      "Perfil en default:true (requisito Producción)",
                      modalChecklist.defaultReady ? "ok" : "blocked",
                    )
                  ) : null}
                  {renderModalChecklistItem(
                    isModalPromotionToProduction
                      ? "Checklist de salida a Producción"
                      : "Confirmación de retorno a Certificación",
                    modalChecklist.flowReady ? "ok" : "pending",
                  )}
                  {renderModalChecklistItem(
                    "Autorización final (clave + palabra)",
                    modalChecklist.authReady ? "ok" : "pending",
                  )}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  Si aplicas esta solicitud, el ambiente quedará preparado para guardar el perfil en{" "}
                  <span className="font-semibold">{modalTargetLabel}</span>.
                </p>
              </div>
            ) : null}

            <div className="mt-5 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleModalBackStep}
                disabled={modalStep <= 1}
                className={BTN_SECONDARY}
              >
                Atrás
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={handleAttemptCloseAmbienteSwitchModal} className={BTN_SECONDARY}>
                  Cancelar
                </button>
                {modalStep < modalTotalSteps ? (
                  <button
                    type="button"
                    onClick={handleModalNextStep}
                    disabled={!canAdvanceModalStep}
                    className={BTN_PRIMARY}
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleApplyAmbienteSwitchFromModal}
                    disabled={!canAdvanceModalStep || !modalChecklist.allReady}
                    className={BTN_PRIMARY}
                  >
                    Aplicar solicitud
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === "integraciones" ? (
        <IntegracionesTab orgUuid={selectedOrgUuid} />
      ) : null}
    </div>
  );
}
